import { Link } from '@tanstack/react-router';
import { CheckCircle2, LinkIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { useResetPassword } from '../hooks/use-reset-password';
import {
  PASSWORD_MIN,
  resetPasswordSchema,
  type ResetPasswordValues,
} from '../schemas/reset-password-schema';

import { AuthCard } from './auth-frame';

export interface ResetPasswordCardProps {
  /** From `?token=` in the emailed link; undefined when the URL was mangled. */
  token: string | undefined;
  /** `welcome` on the set-password link a new user receives (7 days); absent on a reset (15 min). */
  intent: ResetIntent | undefined;
}

export type ResetIntent = 'welcome';

/** Explains the link is unusable and points at the forgot flow, which issues a new one. */
function LinkProblem({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation('auth');
  return (
    <AuthCard id="reset-password" title={title}>
      <div role="status" className="flex flex-col gap-4 text-sm">
        <LinkIcon className="text-warning size-8" aria-hidden />
        <p className="text-muted-foreground leading-relaxed">{body}</p>
        <Button asChild size="lg" className="w-full font-semibold">
          <Link to="/forgot-password">{t('reset.requestNew')}</Link>
        </Button>
      </div>
    </AuthCard>
  );
}

/**
 * Reset and welcome links land here alike (the backend does not tell them apart), so the copy
 * is "choose a password" rather than "reset". A 401 means the token was used or expired.
 */
export function ResetPasswordCard({ token, intent }: ResetPasswordCardProps) {
  const copy = intent === 'welcome' ? 'welcome' : 'reset';
  const { t } = useTranslation('auth');
  const reset = useResetPassword();
  const form = useForm<ResetPasswordValues>({
    resolver: useI18nResolver(resetPasswordSchema),
    defaultValues: { password: '', confirm: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  if (!token) {
    return <LinkProblem title={t('reset.missingTitle')} body={t('reset.missingBody')} />;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await reset.mutateAsync({ token, password: values.password });
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (reset.isSuccess) {
    return (
      <AuthCard id="reset-password" title={t('reset.doneTitle')}>
        <div role="status" className="flex flex-col gap-4 text-sm">
          <CheckCircle2 className="text-success size-8" aria-hidden />
          <p className="text-muted-foreground leading-relaxed">{t('reset.doneBody')}</p>
          <Button asChild size="lg" className="w-full font-semibold">
            <Link to="/login">{t('reset.signIn')}</Link>
          </Button>
        </div>
      </AuthCard>
    );
  }

  if (reset.error instanceof ApiError && reset.error.kind === 'unauthorized') {
    return <LinkProblem title={t('reset.expiredTitle')} body={t(`reset.${copy}.expiredBody`)} />;
  }

  return (
    <AuthCard
      id="reset-password"
      title={t(`reset.${copy}.title`)}
      description={t(`reset.${copy}.description`)}
    >
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {reset.isError && !reset.error.hasFieldErrors ? (
            <ServerError error={reset.error} />
          ) : null}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('reset.newPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    autoFocus
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t('reset.passwordHint', { min: PASSWORD_MIN })}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('reset.confirmPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full font-semibold"
            disabled={reset.isPending}
          >
            {reset.isPending ? t('reset.submitting') : t('reset.submit')}
          </Button>
        </form>
      </Form>
    </AuthCard>
  );
}
